"use client";

import { authClient } from "@/db/auth/client";
import {
  isSafeReturnPath,
  sanitizeReturnUrl,
} from "@garagem/foundation-shared/lib/auth/safe-return-path";
import {
  shouldUseWebReturnBridge,
  webReturnBridgePath,
} from "@/lib/auth/trusted-web-origin";

export { isSafeReturnPath, sanitizeReturnUrl };

/**
 * Performs a redirect after authentication actions.
 * This ensures the session is properly established before navigating.
 *
 * The destination is always sanitized via `sanitizeReturnUrl` to prevent
 * open-redirect attacks via user-controlled `from` / `returnUrl` params.
 *
 * @param url - The URL to redirect to (must be a same-origin path)
 * @param options - Additional options for the redirect
 */
export async function authRedirect(
  url: string,
  options?: {
    fallbackUrl?: string;
    delay?: number;
  }
) {
  const { fallbackUrl = "/", delay = 0 } = options || {};
  const safeFallback = sanitizeReturnUrl(fallbackUrl, "/");
  const safeUrl = sanitizeReturnUrl(url, safeFallback);

  try {
    if (delay > 0) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    window.location.href = safeUrl;
  } catch {
    window.location.href = safeFallback;
  }
}

/**
 * Waits for the session to be available before redirecting.
 * This is useful after login/signup to ensure the session is ready.
 */
export async function authRedirectWithSession(
  url: string,
  options?: {
    fallbackUrl?: string;
    maxRetries?: number;
    retryDelay?: number;
  }
) {
  const {
    fallbackUrl = "/",
    maxRetries = 10,
    retryDelay = 200,
  } = options || {};

  const safeFallback = sanitizeReturnUrl(fallbackUrl, "/");
  const safeUrl = sanitizeReturnUrl(url, safeFallback);

  let retries = 0;

  while (retries < maxRetries) {
    try {
      const session = await authClient.getSession();

      if (session?.data?.session) {
        window.location.href = safeUrl;
        return;
      }
    } catch {
      // Session check failed, retry
    }

    await new Promise((resolve) => setTimeout(resolve, retryDelay));
    retries++;
  }

  window.location.href = safeUrl;
}

/**
 * Redirects to the login page with a return URL.
 * The `returnUrl` is sanitized — non-internal paths are dropped silently.
 */
export function redirectToLogin(returnUrl?: string) {
  const loginUrl = new URL("/entrar", window.location.origin);
  if (returnUrl && isSafeReturnPath(returnUrl)) {
    loginUrl.searchParams.set("from", returnUrl);
  }
  window.location.href = loginUrl.toString();
}

const OAUTH_RETURN_PARAMS = [
  "response_type",
  "client_id",
  "redirect_uri",
  "scope",
  "state",
  "code_challenge",
  "code_challenge_method",
  "exp",
  "sig",
  "nonce",
  "prompt",
] as const;

/**
 * Auth screens that are not themselves a post-login destination. The phone
 * gate must not resume these; it resumes `from` / `returnUrl` / the invite
 * path instead.
 */
const PHONE_GATE_SKIP_CURRENT_PATH = new Set([
  "/entrar",
  "/cadastrar",
  "/esqueci-senha",
  "/redefinir-senha",
  "/verificar-email",
  "/verificar-telefone",
  "/aguardando-confirmacao",
  "/aceitar-termos",
  "/acesso-bloqueado",
]);

function safeParam(searchParams: URLSearchParams, key: string): string | null {
  const value = searchParams.get(key);
  return value && isSafeReturnPath(value) ? value : null;
}

function oauthAuthorizeReturnPath(searchParams: URLSearchParams): string | null {
  if (!searchParams.get("response_type")) return null;
  const oauthUrl = new URL(
    "/api/auth/oauth2/authorize",
    window.location.origin
  );
  for (const key of OAUTH_RETURN_PARAMS) {
    const value = searchParams.get(key);
    if (value) oauthUrl.searchParams.set(key, value);
  }
  // Relative path so it survives `sanitizeReturnUrl`.
  return `${oauthUrl.pathname}${oauthUrl.search}`;
}

/**
 * A safe path that is an actual destination. `/` is the fallback, not a
 * choice — treating it as explicit hides a `returnUrl` invite token.
 */
function chosenReturnPath(value: string | null): string | null {
  return value && value !== "/" ? value : null;
}

/**
 * Explicit post-auth destination already on the query string, or null.
 *
 * Signup and the phone gate store the destination in `returnUrl` (including
 * `/aceitar-convite?token=…`). That param wins over `from`: a leftover `from`
 * (`/` or another page) must not drop the invite token. `from` is the
 * fallback for links that only set it. `returnTo` is the trusted userland
 * bridge and stays first. Unsafe values are dropped.
 */
export function explicitReturnPath(searchParams: URLSearchParams): string | null {
  const returnTo = searchParams.get("returnTo");
  if (returnTo && shouldUseWebReturnBridge(returnTo)) {
    return webReturnBridgePath(returnTo);
  }

  const returnUrl = chosenReturnPath(safeParam(searchParams, "returnUrl"));
  if (returnUrl) return returnUrl;

  const from = chosenReturnPath(safeParam(searchParams, "from"));
  if (from) return from;

  return oauthAuthorizeReturnPath(searchParams);
}

/**
 * Path to resume after WhatsApp OTP.
 *
 * Prefers a trusted `returnTo`, then `returnUrl` (the param signup and this
 * gate write), then `from`. A bare `from=/` does not discard the invite
 * token. Otherwise keeps the current path, so reopening `/aceitar-convite`
 * before OTP finishes still returns there. Auth entry pages are not resumed
 * as themselves.
 */
export function phoneGateReturnPath(
  pathname: string | null | undefined,
  searchParams: URLSearchParams,
): string | null {
  const explicit = explicitReturnPath(searchParams);
  if (explicit && explicit !== "/") return explicit;
  if (!pathname || PHONE_GATE_SKIP_CURRENT_PATH.has(pathname)) return null;
  const query = searchParams.toString();
  const candidate = query ? `${pathname}?${query}` : pathname;
  if (!isSafeReturnPath(candidate) || candidate === "/") return null;
  return candidate;
}

/**
 * Gets the return URL from search params or returns a default.
 *
 * Security: The `from` / `returnUrl` parameters are user-controlled (they
 * come from the query string of /entrar, /cadastrar, /verificar-telefone,
 * etc.). To prevent open-redirect attacks (CWE-601), only same-origin
 * internal paths are accepted; any absolute or protocol-relative URL falls
 * through to the default.
 *
 * Handles OAuth authorize redirects: when better-auth redirects
 * unauthenticated users to /entrar with OAuth params (response_type,
 * client_id, etc.), we reconstruct the /api/auth/oauth2/authorize URL so
 * the flow resumes after login. The returned URL is built from a fixed
 * pathname against `window.location.origin`, so it cannot be coerced to
 * point off-origin.
 */
export function getReturnUrl(searchParams: URLSearchParams, defaultUrl = "/") {
  return explicitReturnPath(searchParams) ?? sanitizeReturnUrl(defaultUrl, "/");
}
